import React, { useMemo } from "react";
import { Box } from "@mui/material";
import WorkLog from "../../models/WorkLog";
import { Token } from "../../theme";

interface Props {
  rows: WorkLog[];
  className?: string;
  expectedExternalHours: number;
  isTimeOff: (row: WorkLog) => boolean;
  isAssignationActive: (row: WorkLog) => boolean;
}

const Total: React.FC<{ label: string; value: number; tone?: string; showHour?: boolean }> = ({ label, value, tone, showHour }) => (
  <Box style={{ gap: "8px", alignItems: "center" }}>
    <div
      style={{
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        color: Token.textTertiary,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontVariantNumeric: "tabular-nums",
        fontSize: 15,
        fontWeight: 600,
        marginTop: 2,
        color: tone ?? Token.textPrimary,
      }}
    >
      {value}
      {showHour && " h"}
    </div>
  </Box>
);

const Divider = () => (
  <div
    style={{
      width: "1px",
      height: "24px",
      backgroundColor: Token.borderDefault,
      opacity: 0.3,
    }}
  />
);

export const WorklogTotals: React.FC<Props> = ({ rows, className, isTimeOff, isAssignationActive }) => {
  const totals = useMemo(() => {
    let hours = 0;
    let internal = 0;
    let missing = 0;

    rows.forEach((row) => {
      hours += row.hours ?? 0;
      internal += row.internalHours ?? 0;

      const notExpected = row.isWeekend || row.isHoliday || row.isPaused || isTimeOff(row) || !isAssignationActive(row);
      if (!notExpected && !row.externalId) missing += 1;
    });

    return { hours, internal, missing };
  }, [rows, isTimeOff, isAssignationActive]);

  return (
    <Box className={className} display="flex" alignItems="center" style={{ gap: 20, flexShrink: 0, whiteSpace: "nowrap" }}>
      <Total label="Hours" value={totals.hours} showHour />
      <Divider />
      <Total label="Internal" value={totals.internal} showHour />
      <Divider />
      <Total label="Missing days" value={totals.missing} tone={totals.missing > 0 ? Token.statusWarningFg : undefined} />
    </Box>
  );
};
